<template>
	<div
		class="overflow-hidden"
		:class="{'w-full': isDisplayableImage || isDisplayableVideo, 'w-fit': !attachment.isUploaded}"
	>
		<div
			class="inline-flex items-center bg-gray-600 border border-gray-500 rounded overflow-hidden"
			:class="[{'bg-opacity-0 hover:bg-opacity-100': !editing}, {'border-red-500': attachment.uploadFailed}]"
		>
			<!-- FAILED -->
			<div v-if="attachment.uploadFailed" class="attachment">
				<BaseIcon name="mono/info" class="text-red-500" />
				<span class="attachment-name" :title="attachment.name">
					{{ attachment.name }}
				</span>
			</div>

			<!-- LOADING -->
			<div v-else-if="attachment.isUploaded === false && !canReachApi" class="attachment">
				<BaseIcon name="mono/load" />
				<span class="attachment-name" :title="attachment.name">
					{{ attachment.name }}
				</span>
			</div>

			<!-- LOADED: IMAGE -->
			<details v-else-if="isDisplayableImage" class="details" open>
				<summary class="attachment cursor-pointer summary w-full" :title="attachment.name">
					<BaseIcon name="mono/photo" class="text-gray-400" />
					<span class="attachment-name">{{ attachment.name }}</span>
					<div class="ml-auto w-14 text-gray-400">
						<BaseIcon name="mono/eye" class="view-icon" />
						<BaseIcon name="mono/eye-slash" class="hide-icon" />
					</div>
					<button v-if="editing" class="remove-button" @click="$emit('remove')">
						<BaseIcon name="mono/delete" class="w-10 h-10 mb-2" />
					</button>
				</summary>
				<a :href="src" target="_blank">
					<img :src="src" loading="lazy" class="mt-2" />
				</a>
			</details>

			<!-- LOADED: IMAGE -->
			<details v-else-if="isDisplayableVideo" class="details" open>
				<summary class="attachment cursor-pointer summary w-full" :title="attachment.name">
					<BaseIcon name="mono/video" class="text-gray-400" />
					<span class="attachment-name">{{ attachment.name }}</span>
					<div class="ml-auto w-14 text-gray-400">
						<BaseIcon name="mono/eye" class="view-icon" />
						<BaseIcon name="mono/eye-slash" class="hide-icon" />
					</div>
					<button v-if="editing" class="remove-button" @click="$emit('remove')">
						<BaseIcon name="mono/delete" class="mb-2 w-10 h-10" />
					</button>
				</summary>
				<video controls>
					<source :src="src" :type="`video/${attachment.name.split('.').at(-1)}`" />
					<div>Your browser does not support this video type.</div>
				</video>
			</details>

			<!-- LOADED: OTHER TYPES  -->
			<button
				v-else
				:disabled="!canBeOpened"
				class="attachment"
				:class="{'hover:text-gray-200': canBeOpened}"
				@click="openAttachment(attachment)"
			>
				<BaseIcon name="mono/attachment" class="text-gray-400" />
				<p class="flex-grow flex-shrink truncate" :title="attachment.name">
					{{ attachment.name }}
				</p>
			</button>
			<button
				v-if="!isDisplayableImage && !isDisplayableVideo && editing"
				class="remove-button"
				@click="$emit('remove')"
			>
				<BaseIcon name="mono/delete" class="w-10 h-10 mb-2" />
			</button>
		</div>

		<!-- Progress bar -->
		<div
			v-if="showProgressBar"
			class="relative my-4 rounded mx-6 h-2"
			:class="[attachment.isUploaded ? 'bg-transparent' : 'bg-gray-500']"
		>
			<div
				class="absolute h-2 rounded"
				:class="[progressBarColorClass]"
				:style="{width: `${attachment.uploadProgressPercentage}%`}"
			/>
		</div>
	</div>
</template>

<script>
import canReachApi from 'shared/mixins/canReachApi'
import isImage from 'shared/utils/isImage'
import isVideo from 'shared/utils/isVideo'

export default {
	name: 'FileAttachment',
	mixins: [canReachApi],
	props: {
		attachment: {
			type: Object,
			required: true,
		},
		editing: {
			type: Boolean,
		},
	},
	emits: ['remove'],
	computed: {
		showProgressBar() {
			if (this.editing !== true) return false
			if (this.attachment.uploadFailed) return false
			if (this.attachment.uploadProgressPercentage === undefined) return false
			return true
		},
		progressBarColorClass() {
			if (this.attachment.isUploaded) return 'bg-transparent'
			if (!this.canReachApi) return 'bg-error'
			return 'bg-blue-500'
		},
		canBeOpened() {
			return this.attachment.isUploaded !== false && this.editing !== true
		},
		isDisplayableImage() {
			// only show image after it's been saved
			if (this.editing && this.attachment.isUploaded === false) return false
			return !this.attachment.uploadedFileName && isImage(this.attachment.name)
		},
		isDisplayableVideo() {
			// only show video after it's been saved
			if (this.editing && this.attachment.isUploaded === false) return false
			return !this.attachment.uploadedFileName && isVideo(this.attachment.name)
		},
		src() {
			return (
				import.meta.env.VUE_APP_API +
				`/storage/open-attachment?attachmentId=${this.attachment.id}&sessionId=${this.$route.params.sessionId}`
			)
		},
	},
	methods: {
		async openAttachment(attachment) {
			window.open(
				import.meta.env.VUE_APP_API +
					`/storage/open-attachment?attachmentId=${attachment.id}&sessionId=${this.$route.params.sessionId}`
			)
		},
	},
}
</script>

<style scoped>
.attachment {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 4px 8px;
	flex-grow: 1;
	flex-shrink: 1;
	overflow: hidden;
	color: var(--gray-300);
}

.attachment-name {
	flex-grow: 1;
	flex-shrink: 1;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.remove-button {
	flex: none;
	padding: 2px 8px;
	margin-left: auto;
	color: var(--gray-400);
}

.remove-button:hover {
	color: var(--gray-300);
}

.hide-icon,
.view-icon {
	display: none !important;
}

.details:not([open]) > .summary:hover .view-icon {
	display: block !important;
}

.details[open] > .summary:hover .hide-icon {
	display: block !important;
}
</style>
